import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Alert from '../Alert/Alert';
import store, { setGlobalEmail } from '../../store/store';
import EmailConfirmationForm from './EmailConfirmationForm';

const EmailEditForm = ({ data, setAllEditing, isEditingEmail, setIsEditingEmail }) => {
    const dispatch = useDispatch();
    const [email, setEmail] = useState(data?.Email || '');
    const [emailConfirmed, setEmailConfirmed] = useState(Boolean(data?.EmailConfirmed));
    const [error, setError] = useState('');
    const [codeError, setCodeError] = useState('');
    const [loading, setLoading] = useState(false);
    const [emailConfirmationFormVisable, setEmailConfirmationFormVisable] = useState(false);
    const [alertVisible, setAlertVisible] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('error');
    const [showConfirmEmailButton, setShowConfirmEmailButton] = useState(false);
    const [confirmationCode, setConfirmationCode] = useState('');
    const [changeEmailCode, setChangeEmailCode] = useState('');

    useEffect(() => {
        dispatch(setGlobalEmail(data?.Email || ''));
    }, [data, dispatch]);

    const showAlert = (message, type = 'error') => {
        setAlertMessage(message);
        setAlertType(type);
        setAlertVisible(true);
        setTimeout(() => setAlertVisible(false), 3000);
    };

    const cancelDataChange = (e) => {
        e.preventDefault();
        setEmail((data?.Email || '').toLowerCase());
        setIsEditingEmail(false);
    };

    const handleConfirmCode = async () => {
        if (!confirmationCode) {
            setCodeError('Введите код подтверждения');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await fetch('/Account/ConfirmEmail', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: confirmationCode }),
            });
            const resp = await response.json();

            if (resp.success) {
                data.EmailConfirmed = true;
                setEmailConfirmed(true);
                showAlert('Email успешно подтверждён', 'success');
            } else {
                setCodeError(resp.message || 'Неверный код');
            }
        } catch (err) {
            setError('Ошибка сети');
        }

        setLoading(false);
    };

    const handleApproveEmail = async (e, { confirmationMethod, body }, onCodeSent, cooldown) => {
        e.preventDefault();
        if (cooldown > 0) {
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(confirmationMethod, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body,
            });
            const responseData = await response.json();

            if (responseData.success) {
                onCodeSent(responseData);
            } else {
                showAlert(responseData.message || 'Произошла ошибка');
            }
        } catch (err) {
            showAlert('Ошибка сети');
        }

        setLoading(false);
    };

    const handleChangeConfirmedEmail = async () => {
        if (!changeEmailCode) {
            setCodeError('Введите код подтверждения');
            return;
        }

        try {
            const response = await fetch('/Account/ChangeEmail', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: changeEmailCode, email }),
            });
            const resp = await response.json();

            if (resp.success) {
                data.Email = email;
                dispatch(setGlobalEmail(email));
                setEmailConfirmed(false);
                setIsEditingEmail(false);
                showAlert('Email успешно изменён', 'success');
            } else {
                setCodeError(resp.message || 'Неверный код');
            }
        } catch (err) {
            setError('Ошибка сети');
        }
    };

    const handleEmailSubmit = async () => {
        try {
            const response = await fetch('/Account/ChangeEmail', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            const resp = await response.json();

            if (resp.success) {
                data.Email = email;
                dispatch(setGlobalEmail(email));
                setIsEditingEmail(false);
                showAlert('Email успешно изменён', 'success');
            } else {
                setError(resp.message || 'Произошла ошибка');
            }
        } catch (err) {
            setError('Ошибка сети');
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        if (!email) {
            setError('Заполните email');
            return;
        }

        if (email === data?.Email) {
            setError('Нельзя изменить email на текущий');
            return;
        }

        setLoading(true);
        setError('');
        setCodeError('');

        if (emailConfirmed) {
            await handleChangeConfirmedEmail();
        } else {
            await handleEmailSubmit();
        }

        setLoading(false);
    };

    return (
        <div className="account-panel">
            {alertVisible && (
                <Alert message={alertMessage} type={alertType} onClose={() => setAlertVisible(false)} />
            )}

            <div className="account-panel__header">
                <div>
                    <h3 className="account-panel__title">Email</h3>
                    <p className="account-panel__subtitle">Текущий адрес для авторизации, уведомлений и подтверждения заказов.</p>
                </div>
                <span className={`account-status ${emailConfirmed ? 'is-success' : 'is-warning'}`}>
                    {emailConfirmed ? 'Подтверждён' : 'Не подтверждён'}
                </span>
            </div>

            {isEditingEmail ? (
                <form onSubmit={handleFormSubmit} className="account-form">
                    <div className="account-field">
                        <label className="account-field__label" htmlFor="account-email">
                            Новый email
                        </label>
                        <input
                            id="account-email"
                            type="email"
                            name="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value.toLowerCase())}
                            placeholder="Укажите новый email"
                            className="account-input"
                        />
                    </div>

                    {error && <p className="account-error">{error}</p>}

                    {emailConfirmed && (
                        <>
                            <p className="account-note">
                                Для изменения подтверждённого email мы сначала отправим код подтверждения на текущий адрес.
                            </p>
                            <EmailConfirmationForm
                                handleSendCode={handleApproveEmail}
                                confirmationCode={changeEmailCode}
                                setConfirmationCode={setChangeEmailCode}
                                sendCodeButtonText="Отправить код"
                                error={codeError}
                                sendCodeData={{ confirmationMethod: '/Account/SendEmailChange' }}
                            />
                        </>
                    )}

                    <div className="account-actions">
                        <button type="submit" className="account-button" disabled={loading}>
                            {loading ? 'Загрузка...' : 'Сохранить'}
                        </button>
                        <button className="account-button--ghost" onClick={cancelDataChange} type="button">
                            Отмена
                        </button>
                    </div>
                </form>
            ) : (
                <div className="account-stack">
                    <div className="account-meta-row">
                        <span className="account-meta-label">Email</span>
                        <span className="account-meta-value">{email || 'Не указан'}</span>
                    </div>

                    {!emailConfirmed && (
                        <div className="account-confirmation">
                            <button
                                className="account-button--ghost"
                                onClick={() => setEmailConfirmationFormVisable((prev) => !prev)}
                                type="button"
                            >
                                {!emailConfirmationFormVisable ? 'Подтвердить email' : 'Отмена'}
                            </button>

                            {emailConfirmationFormVisable && (
                                <>
                                    <EmailConfirmationForm
                                        handleSendCode={handleApproveEmail}
                                        confirmationCode={confirmationCode}
                                        setConfirmationCode={setConfirmationCode}
                                        sendCodeButtonText="Отправить код"
                                        error={codeError}
                                        sendCodeData={{ confirmationMethod: '/Account/SendEmailConfirmation' }}
                                        onFirstCodeSent={() => setShowConfirmEmailButton(true)}
                                    />

                                    {showConfirmEmailButton && (
                                        <button onClick={handleConfirmCode} className="account-button" type="button">
                                            {loading ? 'Загрузка...' : 'Подтвердить email'}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    <div className="account-actions">
                        <button
                            onClick={() => {
                                setAllEditing(false);
                                setIsEditingEmail(true);
                            }}
                            className="account-button--ghost"
                            type="button"
                        >
                            Изменить email
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmailEditForm;
